/**
 * The one session, shared.
 *
 * Its own module rather than living in `App.tsx`, so a screen can read the
 * session without importing the router that renders it — which would be a
 * cycle.
 */

import { createContext, useContext } from 'react';
import type { Session } from '@heroiclabs/nakama-js';
import type { ClientConfig } from './net/session.js';

export interface SessionState {
  session: Session | null;
  config: ClientConfig | null;
  ready: boolean;
  /** Take on a new session after signing in, registering or linking. */
  adopt: (session: Session) => Promise<void>;
}

export const SessionContext = createContext<SessionState>({
  session: null,
  config: null,
  ready: false,
  adopt: async () => undefined,
});

export function useSession(): SessionState {
  return useContext(SessionContext);
}

/**
 * Does this session belong to a real account, or a guest?
 *
 * Answered by the server, which reads the account rather than guessing from
 * the client's own history — a browser that once registered and later cleared
 * storage would otherwise believe the wrong thing.
 */
export function hasAccount(state: SessionState): boolean {
  return state.config?.account?.hasEmail === true;
}
